import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import DescriptionCell from '../components/DescriptionCell';
import SubscribeModal from '../components/SubscribeModal';
import './Categories.css';


const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const ROOT_MENU_ID = 1;

const resolveImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

const formatPrice = (price) =>
  price != null ? `₹${parseFloat(price).toFixed(2)}` : null;

const formatCutoffTime = (t) => (t ? t.slice(0, 5) : null);

const isPastCutoff = (cutoffTimeStr, orderAfterCutoff) => {
  if (!cutoffTimeStr || orderAfterCutoff === 'Y') return false;
  const [h, m] = cutoffTimeStr.split(':').map(Number);
  const cutoff = new Date();
  cutoff.setHours(h, m, 0, 0);
  return new Date() > cutoff;
};

const isAfterCutoffAllowed = (cutoffTimeStr, orderAfterCutoff) => {
  if (!cutoffTimeStr || orderAfterCutoff !== 'Y') return false;
  const [h, m] = cutoffTimeStr.split(':').map(Number);
  const cutoff = new Date();
  cutoff.setHours(h, m, 0, 0);
  return new Date() > cutoff;
};

// Returns { sat, sun } for the next Saturday on-or-after baseDate, plus the following Sunday
const nextWeekend = (baseDate) => {
  const d = new Date(baseDate);
  const dow = d.getDay();
  const daysToSat = (6 - dow + 7) % 7;
  const daysToSun = (7 - dow) % 7;
  const satDate = new Date(d);
  satDate.setDate(d.getDate() + daysToSat);
  const sunDate = new Date(d);
  sunDate.setDate(d.getDate() + daysToSun);
  return satDate <= sunDate
    ? { sat: satDate, sun: sunDate }
    : { sat: sunDate, sun: satDate };
};

const fmtDate = (d) => d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });

const computeDeliveryLabel = (product) => {
  const lead = parseInt(product.order_lead_days, 10) || 0;
  const afterCutoff = isAfterCutoffAllowed(product.order_cutoff_time, product.order_after_cutoff);

  if (product.days_available === 'WE') {
    // Weekend products: find next Sat+Sun on or after (today + lead + 1 if past cutoff)
    const base = new Date();
    base.setDate(base.getDate() + lead + (afterCutoff ? 1 : 0));
    const { sat, sun } = nextWeekend(base);
    return `${fmtDate(sat)} & ${fmtDate(sun)}`;
  }

  // AD (all days): existing logic â€” only label when past cutoff and order_after_cutoff='Y'
  if (!afterCutoff) return null;
  const date = new Date();
  date.setDate(date.getDate() + lead + 1);
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};


// â”€â”€ Level-1 tile grid â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const RootTiles = ({ menus, loading, onSelect }) => {
  if (loading) return (
    <div className="cat-state">
      <div className="cat-spinner" />
      <p>Loading categories…</p>
    </div>
  );

  if (menus.length === 0) return (
    <div className="cat-state"><p>No categories available.</p></div>
  );

  return (
    <div className="cat-root-grid">
      {menus.map((menu) => (
        <div key={menu.id} className="cat-root-item">
          {menu.header_text && (
            <p className="cat-root-header-text" onClick={() => onSelect(menu)} style={{ cursor: 'pointer' }}>{menu.header_text}</p>
          )}
          <button className="cat-root-tile" onClick={() => onSelect(menu)}>
            <div className="cat-root-image-wrap">
              {menu.image_url
                ? <img src={resolveImg(menu.image_url)} alt={menu.menu_name} className="cat-root-image" />
                : <div className="cat-root-image-placeholder">&#128230;</div>
              }
            </div>
            <p className="cat-root-name">{menu.menu_name}</p>
          </button>
        </div>
      ))}
    </div>
  );
};

// â”€â”€ Sub-level tab row â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const TabRow = ({ menus, selectedId, onSelect, loading }) => {
  if (loading) return (
    <div className="cat-tabrow cat-tabrow--loading">
      <span className="cat-tab-spinner" />
    </div>
  );

  return (
    <div className="cat-tabrow">
      <button
        className={`cat-tab ${selectedId === 'all' ? 'cat-tab--active' : ''}`}
        onClick={() => onSelect('all')}
      >
        <span className="cat-tab-icon cat-tab-icon--all">&#9733;</span>
        <span className="cat-tab-label">All</span>
      </button>

      {menus.map((menu) => (
        <button
          key={menu.id}
          className={`cat-tab ${selectedId === menu.id ? 'cat-tab--active' : ''}`}
          onClick={() => onSelect(menu.id)}
        >
          <span className="cat-tab-icon">
            {menu.image_url
              ? <img src={resolveImg(menu.image_url)} alt={menu.menu_name} />
              : <span className="cat-tab-icon--placeholder">&#128230;</span>
            }
          </span>
          <span className="cat-tab-label">{menu.menu_name}</span>
        </button>
      ))}
    </div>
  );
};

// â”€â”€ Shared page-list renderer â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const PageList = ({ pages }) => {
  if (!pages || pages.length === 0) return null;
  return (
    <div className="cat-menu-pages">
      {pages.map((page) => {
        // Header + Banner type + has text â†’ horizontal scrolling ticker, no image
        if (page.page_type === 'Header' && page.static_banner === 'B' && page.display_text) {
          return (
            <div key={page.id} className="cat-banner-scroll">
              <span className="cat-banner-scroll-text">{page.display_text}</span>
            </div>
          );
        }
        // Default: image + text stacked
        const ctaHref = page.cta_type === 'whatsapp'
          ? `https://wa.me/${page.cta_value}${page.cta_message ? '?text=' + encodeURIComponent(page.cta_message) : ''}`
          : page.cta_type === 'url' ? page.cta_value : null;

        return (
          <div key={page.id} className="cat-menu-page">
            {page.image_url && (
              <img
                src={resolveImg(page.image_url)}
                alt={page.display_text || ''}
                className="cat-menu-page-img"
              />
            )}
            {page.display_text && (
              <p className="cat-menu-page-text">{page.display_text}</p>
            )}
            {ctaHref && (
              <a
                href={ctaHref}
                target="_blank"
                rel="noopener noreferrer"
                className={`cat-menu-page-cta${page.cta_type === 'whatsapp' ? ' cat-menu-page-cta--whatsapp' : ''}`}
              >
                {page.cta_type === 'whatsapp' && <span className="cat-cta-wa-icon">&#128172;</span>}
                {page.cta_label || (page.cta_type === 'whatsapp' ? 'Chat on WhatsApp' : 'Visit Website')}
              </a>
            )}
          </div>
        );
      })}
    </div>
  );
};

// â”€â”€ Image-only grid â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ImageOnlyGrid = ({ products, user, isLive = false }) => {
  const { addToCart, removeFromCart, getQuantity } = useCart();
  const [subscribingProduct, setSubscribingProduct] = useState(null);

  return (
    <>
      <div className="cat-img-only-grid">
        {products.map((product) => {
          const qty = getQuantity(product.id);
          const blocked = isPastCutoff(product.order_cutoff_time, product.order_after_cutoff);
          return (
            <div key={product.id} className="cat-img-only-card">
              <div className="cat-img-only-image-wrap">
                {product.image_url
                  ? <img src={resolveImg(product.image_url)} alt={product.name} className="cat-img-only-image" />
                  : <div className="cat-img-only-placeholder">&#128230;</div>
                }
              </div>
              <div className="cat-img-only-footer">
                <div className="cat-img-only-meta">
                  <span className="cat-img-only-name">{product.name}</span>
                  <div className="cat-img-only-price-row">
                    {formatPrice(product.price) && (
                      <span className="cat-img-only-price">{formatPrice(product.price)}</span>
                    )}
                    {formatCutoffTime(product.order_cutoff_time) && (
                      <span className="cat-img-only-cutoff">Order By {formatCutoffTime(product.order_cutoff_time)}</span>
                    )}
                  </div>
                </div>
                <div className="cat-img-only-qty">
                  <button
                    className="qty-btn qty-btn--minus"
                    onClick={() => removeFromCart(product.id)}
                    disabled={qty === 0 || blocked}
                    aria-label="Remove one"
                  >&#8722;</button>
                  <span className="qty-value">{qty}</span>
                  <button
                    className="qty-btn qty-btn--plus"
                    onClick={() => addToCart(product)}
                    disabled={blocked || (isLive && product.current_stock != null && qty >= parseInt(product.current_stock, 10))}
                    aria-label="Add one"
                  >&#43;</button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {subscribingProduct && user && (
        <SubscribeModal
          product={subscribingProduct}
          user={user}
          onClose={() => setSubscribingProduct(null)}
        />
      )}
    </>
  );
};

// â”€â”€ Product grid â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
const ProductGrid = ({ products, loading, error, menuPages = [], user, isLive = false }) => {
  const { addToCart, removeFromCart, getQuantity } = useCart();
  const [subscribingProduct, setSubscribingProduct] = useState(null);

  if (loading) return (
    <div className="cat-state">
      <div className="cat-spinner" />
      <p>Loading products…</p>
    </div>
  );

  if (error) return (
    <div className="cat-state cat-state--error"><p>{error}</p></div>
  );

  const headerPages = menuPages.filter((p) => p.page_type === 'Header');
  const bodyPages   = menuPages.filter((p) => p.page_type === 'Body');
  const footerPages = menuPages.filter((p) => p.page_type === 'Footer');

  // Body pages always shown first; products follow if any
  const noContent = bodyPages.length === 0 && products.length === 0;

  const useImageOnly = products.some((p) => p.show_image_only);

  const productGrid = products.length > 0 && bodyPages.length === 0 ? (
    useImageOnly ? (
      <ImageOnlyGrid products={products} user={user} isLive={isLive} />
    ) : (
    <div className="cat-product-grid">
      {products.map((product) => {
        const qty = getQuantity(product.id);
        const blocked = isPastCutoff(product.order_cutoff_time, product.order_after_cutoff);
        const deliveryLabel = qty > 0 ? computeDeliveryLabel(product) : null;
        return (
          <div key={product.id} className="cat-tile">
            <div className="cat-tile-image-wrap">
              {product.image_url
                ? <img src={resolveImg(product.image_url)} alt={product.name} className="cat-tile-image" />
                : <div className="cat-tile-image-placeholder">&#128230;</div>
              }
            </div>
            <div className="cat-tile-info">
              <h3 className="cat-tile-name">{product.name}</h3>
              {product.description && (
                <DescriptionCell text={product.description} className="cat-tile-desc" />
              )}
              <div className="cat-tile-price-row">
                {formatPrice(product.price) && (
                  <span className="cat-tile-price">{formatPrice(product.price)}</span>
                )}
                {formatCutoffTime(product.order_cutoff_time) && (
                  <span className="cat-tile-cutoff">
                    Order By {formatCutoffTime(product.order_cutoff_time)}
                  </span>
                )}
              </div>
            </div>
            <div className="cat-tile-qty">
              <button
                className="qty-btn qty-btn--minus"
                onClick={() => removeFromCart(product.id)}
                disabled={qty === 0 || blocked}
                aria-label="Remove one"
              >&#8722;</button>
              <span className="qty-value">{qty}</span>
              <button
                className="qty-btn qty-btn--plus"
                onClick={() => addToCart(product)}
                disabled={blocked || (isLive && product.current_stock != null && qty >= parseInt(product.current_stock, 10))}
                aria-label="Add one"
              >&#43;</button>
              {deliveryLabel && (
                <span className="cat-tile-delivery-label">Delivery: {deliveryLabel}</span>
              )}
              {user?.subscription_allowed && (
                <button
                  className="qty-btn qty-btn--subscribe"
                  onClick={() => setSubscribingProduct(product)}
                  title="Subscribe"
                  aria-label="Subscribe"
                >&#128197;</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
    )
  ) : null;

  return (
    <>
      <PageList pages={headerPages} />
      {bodyPages.length > 0 && <PageList pages={bodyPages} />}
      {noContent && <div className="cat-state"><p>No products available.</p></div>}
      {productGrid}
      <PageList pages={footerPages} />
      {subscribingProduct && user && (
        <SubscribeModal
          product={subscribingProduct}
          user={user}
          onClose={() => setSubscribingProduct(null)}
        />
      )}
    </>
  );
};

// â”€â”€ Main Categories page â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
// ── Pack Customise Modal ──────────────────────────────────────────────────────
// Stable cart key per unique combination of selections
const comboCartId = (menuPackId, sel) => {
  const sig = Object.entries(sel)
    .filter(([, q]) => q > 0)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([pid, q]) => `${pid}:${q}`)
    .join(',');
  return `pack_${menuPackId}_${sig}`;
};

const PackCustomiseModal = ({ pack, user, onClose }) => {
  const { addToCart, cart } = useCart();

  // All combos in cart that belong to this pack, sorted newest first
  const packCombos = Object.values(cart)
    .filter(item => item.product.isPack && item.product.menu_pack_id === pack.menu_pack_id)
    .sort((a, b) => (b.product.addedAt || 0) - (a.product.addedAt || 0));
  const lastCombo  = packCombos[0];
  const prevSel     = lastCombo?.product?.selections;
  const prevSummary = lastCombo?.product?.selectionSummary;

  const authHeader = { Authorization: `Bearer ${user?.token}` };
  const [groups, setGroups]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [selections, setSelections] = useState({});

  useEffect(() => {
    axios.get(`${API_URL}/menuRoutes/pack-details`,
      { params: { menu_pack_id: pack.menu_pack_id }, headers: authHeader })
      .then(r => {
        const loaded = r.data;
        setGroups(loaded);
        // Auto-select single-product groups at multiples_of qty
        setSelections(prev => {
          const next = { ...prev };
          for (const group of loaded) {
            if (group.products.length === 1) {
              next[group.products[0].product_id] = pack.multiples_of;
            }
          }
          return next;
        });
        setLoading(false);
      })
      .catch(() => { setError('Failed to load pack details.'); setLoading(false); });
  }, [pack.menu_pack_id]); // eslint-disable-line

  const totalSelected = Object.values(selections).reduce((s, q) => s + q, 0);
  const totalRequired = groups.length * pack.multiples_of;

  // Show Repeat Last whenever there is any previous combo for this pack
  const canRepeat = !loading && groups.length > 0 && !!prevSel;

  const groupTotal = (groupId) => {
    const g = groups.find(gr => gr.group_id === groupId);
    if (!g) return 0;
    return g.products.reduce((s, p) => s + (selections[p.product_id] || 0), 0);
  };

  const uniqueInGroup = (groupId) => {
    const g = groups.find(gr => gr.group_id === groupId);
    if (!g) return 0;
    return g.products.filter(p => (selections[p.product_id] || 0) > 0).length;
  };

  // multiples_of is per-group; unique limit caps distinct products chosen from a group
  const canIncrement = (group, productId) =>
    groupTotal(group.group_id) < pack.multiples_of &&
    (group.max_unique == null ||
      (selections[productId] || 0) > 0 ||
      uniqueInGroup(group.group_id) < group.max_unique);

  const adjust = (productId, delta, group) => {
    setSelections(prev => {
      if (group?.max_unique === 1) {
        if (delta > 0) return { ...prev, [productId]: pack.multiples_of };
        const { [productId]: _, ...rest } = prev; return rest;
      }
      const cur  = prev[productId] || 0;
      const next = Math.max(0, cur + delta);
      if (next === 0) { const { [productId]: _, ...rest } = prev; return rest; }
      return { ...prev, [productId]: next };
    });
  };

  const handleAddPack = () => {
    const selectionSummary = groups.map(g => ({
      group_id:  g.group_id,
      groupName: g.group_name,
      items: g.products
        .filter(p => (selections[p.product_id] || 0) > 0)
        .map(p => ({ product_id: p.product_id, name: p.product_name, qty: selections[p.product_id] })),
    }));
    addToCart({
      id:                 comboCartId(pack.menu_pack_id, selections),
      name:               pack.pack_name,
      price:              pack.price,
      isPack:             true,
      pack_id:            pack.pack_id,
      menu_pack_id:       pack.menu_pack_id,
      multiples_of:       pack.multiples_of,
      order_lead_days:    pack.order_lead_days,
      order_cutoff_time:  pack.order_cutoff_time,
      order_after_cutoff: pack.order_after_cutoff,
      days_available:     pack.days_available,
      image_url:          pack.image_url,
      selections,
      selectionSummary,
      addedAt:            Date.now(),
    });
    onClose();
  };

  const handleRepeatLast = () => {
    addToCart({
      id:                 comboCartId(pack.menu_pack_id, prevSel),
      name:               pack.pack_name,
      price:              pack.price,
      isPack:             true,
      pack_id:            pack.pack_id,
      menu_pack_id:       pack.menu_pack_id,
      multiples_of:       pack.multiples_of,
      order_lead_days:    pack.order_lead_days,
      order_cutoff_time:  pack.order_cutoff_time,
      order_after_cutoff: pack.order_after_cutoff,
      days_available:     pack.days_available,
      image_url:          pack.image_url,
      selections:         prevSel,
      selectionSummary:   prevSummary,
      addedAt:            Date.now(),
    });
    onClose();
  };

  const colClass = groups.length <= 2 ? 'pack-modal--2col'
                 : groups.length === 3 ? 'pack-modal--3col'
                 : 'pack-modal--4col';

  return (
    <div className="pack-overlay" onClick={onClose}>
      <div className={`pack-modal ${colClass}`} onClick={e => e.stopPropagation()}>
        <div className="pack-modal-hdr">
          <div>
            <h2>{pack.pack_name}</h2>
            <p>Pick {pack.multiples_of} from each group &nbsp;&middot;&nbsp; {formatPrice(pack.price)}</p>
          </div>
          <button className="pack-modal-close-btn" onClick={onClose}>&#10005;</button>
        </div>

        {loading && <div className="cat-state"><div className="cat-spinner" /><p>Loading&hellip;</p></div>}
        {error   && <div className="cat-state cat-state--error"><p>{error}</p></div>}

        {!loading && !error && (
          <>
            <div className="pack-modal-body">
              {groups.map(group => (
                <div key={group.group_id} className="pack-group-col">
                  <div className="pack-group-hdr">
                    <span className="pack-group-name">{group.group_name}</span>
                    {group.max_unique && (
                      <span className="pack-group-hint">Any {group.max_unique}</span>
                    )}
                  </div>
                  <div className="pack-group-items">
                    {group.products.map(prod => {
                      const qty = selections[prod.product_id] || 0;
                      const isAuto = group.products.length === 1;
                      return (
                        <div key={prod.product_id} className={`pack-prod-card${isAuto ? ' pack-prod-card--auto' : ''}`}>
                          <div className="pack-prod-img-wrap">
                            {prod.image_url
                              ? <img src={resolveImg(prod.image_url)} alt={prod.product_name} className="pack-prod-img" />
                              : <div className="pack-prod-placeholder">&#128230;</div>
                            }
                          </div>
                          <div className="pack-prod-info">
                            <span className="pack-prod-name">{prod.product_name}</span>
                            {prod.price && <span className="pack-prod-price">{formatPrice(prod.price)}</span>}
                          </div>
                          <div className={`pack-prod-qty${isAuto ? ' pack-prod-qty--auto' : ''}`}>
                            <button className="qty-btn qty-btn--minus"
                              onClick={() => adjust(prod.product_id, -1, group)}
                              disabled={qty === 0 || isAuto}
                              aria-label="Remove one">&#8722;</button>
                            <span className="qty-value">{qty}</span>
                            <button className="qty-btn qty-btn--plus"
                              onClick={() => adjust(prod.product_id, 1, group)}
                              disabled={!canIncrement(group, prod.product_id) || isAuto}
                              aria-label="Add one">&#43;</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            <div className="pack-modal-footer">
              <div className="pack-tally">
                <span className={`pack-tally-count${totalSelected === totalRequired ? ' pack-tally-count--full' : ''}`}>
                  {totalSelected}&nbsp;/&nbsp;{totalRequired}
                </span>
                <span className="pack-tally-label">items selected</span>
              </div>
              <div className="pack-footer-actions">
                <button className="pack-btn pack-btn--cancel" onClick={onClose}>Cancel</button>
                {canRepeat && (
                  <button className="pack-btn pack-btn--repeat" onClick={handleRepeatLast}>
                    Repeat Last
                  </button>
                )}
                <button className="pack-btn pack-btn--add"
                  onClick={handleAddPack}
                  disabled={totalSelected !== totalRequired}>
                  Add Pack
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Pack Grid ─────────────────────────────────────────────────────────────────
const PackGrid = ({ packs, user }) => {
  const { removeFromCart, cart }    = useCart();
  const [activePack, setActivePack] = useState(null);

  if (!packs || packs.length === 0) return null;

  return (
    <>
      <div className="cat-product-grid cat-pack-section">
        {packs.map(pack => {
          // All cart combos for this pack, newest first
          const combos = Object.values(cart)
            .filter(item => item.product.isPack && item.product.menu_pack_id === pack.menu_pack_id)
            .sort((a, b) => (b.product.addedAt || 0) - (a.product.addedAt || 0));
          const qty     = combos.reduce((s, c) => s + c.quantity, 0);
          const lastId  = combos[0]?.product?.id;

          const blocked       = isPastCutoff(pack.order_cutoff_time, pack.order_after_cutoff);
          const deliveryLabel = qty > 0 ? computeDeliveryLabel({
            order_lead_days:    pack.order_lead_days,
            order_cutoff_time:  pack.order_cutoff_time,
            order_after_cutoff: pack.order_after_cutoff,
            days_available:     pack.days_available,
          }) : null;

          return (
            <div key={pack.menu_pack_id} className={`cat-tile${blocked ? ' cat-tile--blocked' : ''}`}>
              <div className="cat-tile-image-wrap cat-pack-badge-wrap">
                {pack.image_url
                  ? <img src={resolveImg(pack.image_url)} alt={pack.pack_name} className="cat-tile-image" />
                  : <div className="cat-tile-image-placeholder">&#127873;</div>
                }
                <span className="cat-pack-badge">Pack</span>
              </div>
              <div className="cat-tile-info">
                <h3 className="cat-tile-name">{pack.pack_name}</h3>
                {pack.description && <p className="cat-tile-desc">{pack.description}</p>}
                <div className="cat-tile-price-row">
                  {formatPrice(pack.price) && <span className="cat-tile-price">{formatPrice(pack.price)}</span>}
                  {formatCutoffTime(pack.order_cutoff_time) && (
                    <span className="cat-tile-cutoff">Order By {formatCutoffTime(pack.order_cutoff_time)}</span>
                  )}
                </div>
              </div>
              <div className="cat-tile-qty">
                {qty > 0 && (
                  <>
                    <button className="qty-btn qty-btn--minus"
                      onClick={() => removeFromCart(lastId)}
                      aria-label="Remove one">&#8722;</button>
                    <span className="qty-value">{qty}</span>
                  </>
                )}
                <button
                  className={`pack-customise-btn${qty > 0 ? ' pack-customise-btn--more' : ''}`}
                  onClick={() => setActivePack(pack)}
                  disabled={blocked}>
                  {qty > 0 ? '+ Add More' : 'Customise & Add'}
                </button>
                {deliveryLabel && (
                  <span className="cat-tile-delivery-label">Delivery: {deliveryLabel}</span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {activePack && (
        <PackCustomiseModal
          pack={activePack}
          user={user}
          onClose={() => setActivePack(null)}
        />
      )}
    </>
  );
};

const Categories = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const authHeader = { Authorization: `Bearer ${user?.token}` };

  // Level-1 menus (shown as tiles)
  const [rootMenus, setRootMenus] = useState([]);
  const [liveRootIds, setLiveRootIds] = useState(null); // null = not in live mode or not yet loaded
  const [rootLoading, setRootLoading] = useState(true);
  const [rootBannerPages, setRootBannerPages] = useState([]);

  // null = tiles view; object = { id, menu_name } of selected level-1 tile
  const [selectedRoot, setSelectedRoot] = useState(null);

  // Tab drill-down state (active only when selectedRoot != null)
  const [path, setPath] = useState([]);                 // [{ parentId, selectedId }]
  const [menuMap, setMenuMap] = useState({});            // parentId -> children[]
  const [menuLoading, setMenuLoading] = useState({});    // parentId -> bool

  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);
  const [packs, setPacks] = useState([]);
  const [menuPages, setMenuPages] = useState([]);

  // Search bar state
  const [searchText, setSearchText] = useState('');
  const [searchProducts, setSearchProducts] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const searchInputRef = useRef(null);

  // Shrink search bar on scroll
  useEffect(() => {
    const scrollTarget = document.querySelector('.main-content') || window;
    const handleScroll = () => {
      const top = scrollTarget === window ? window.scrollY : scrollTarget.scrollTop;
      setScrolled(top > 60);
    };
    scrollTarget.addEventListener('scroll', handleScroll);
    return () => scrollTarget.removeEventListener('scroll', handleScroll);
  }, []);

  // Debounced product search
  useEffect(() => {
    if (!searchText.trim()) {
      setSearchProducts([]);
      setSearchError(null);
      return;
    }
    const liveMode = user?.user_type === 'S' &&
      (localStorage.getItem('checkoutMode') || 'live') === 'live';
    setSearchLoading(true);
    setSearchError(null);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/menuRoutes/products`, {
          params: {
            root_menu_id: ROOT_MENU_ID,
            search: searchText,
            ...(liveMode && { live_stock: 'true' }),
          },
          headers: authHeader,
        });
        setSearchProducts(res.data);
      } catch {
        setSearchError('Failed to load products. Please try again.');
      } finally {
        setSearchLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText]);

  const handleSearchClear = () => {
    setSearchText('');
    searchInputRef.current?.focus();
  };

  // Fetch children for a parentId; returns the array
  const fetchChildren = useCallback(async (parentId) => {
    if (menuMap[parentId] !== undefined) return menuMap[parentId];
    setMenuLoading((p) => ({ ...p, [parentId]: true }));
    try {
      const res = await axios.get(`${API_URL}/menuRoutes/children`, {
        params: { parent_id: parentId },
        headers: authHeader,
      });
      const children = res.data;
      setMenuMap((p) => ({ ...p, [parentId]: children }));
      return children;
    } catch {
      setMenuMap((p) => ({ ...p, [parentId]: [] }));
      return [];
    } finally {
      setMenuLoading((p) => ({ ...p, [parentId]: false }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuMap, user?.token]);

  const isLive = user?.user_type === 'S' &&
    (localStorage.getItem('checkoutMode') || 'live') === 'live';

  const fetchProducts = useCallback(async (scopeMenuId) => {
    setProductsLoading(true);
    setProductsError(null);
    setMenuPages([]);
    setPacks([]);
    const liveMode = user?.user_type === 'S' &&
      (localStorage.getItem('checkoutMode') || 'live') === 'live';
    try {
      const [prodRes, pagesRes] = await Promise.all([
        axios.get(`${API_URL}/menuRoutes/products`, {
          params: { menu_id: scopeMenuId, ...(liveMode && { live_stock: 'true' }) },
          headers: authHeader,
        }),
        axios.get(`${API_URL}/menuRoutes/menu-pages`, {
          params: { menu_id: scopeMenuId },
          headers: authHeader,
        }),
      ]);
      setProducts(prodRes.data);
      setMenuPages(pagesRes.data);
    } catch {
      setProductsError('Failed to load products. Please try again.');
    } finally {
      setProductsLoading(false);
    }
    // Packs are non-fatal — silently ignored if the endpoint is unavailable
    axios.get(`${API_URL}/menuRoutes/menu-packs`,
      { params: { menu_id: scopeMenuId }, headers: authHeader })
      .then(r => setPacks(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.token]);

  // On mount: load Level-1 tiles and root menu banner pages in parallel
  useEffect(() => {
    (async () => {
      setRootLoading(true);
      const liveMode = user?.user_type === 'S' &&
        (localStorage.getItem('checkoutMode') || 'live') === 'live';
      try {
        const requests = [
          axios.get(`${API_URL}/menuRoutes/children`, {
            params: { parent_id: ROOT_MENU_ID },
            headers: authHeader,
          }),
          axios.get(`${API_URL}/menuRoutes/menu-pages`, {
            params: { menu_id: ROOT_MENU_ID },
            headers: authHeader,
          }),
        ];
        if (liveMode) {
          requests.push(
            axios.get(`${API_URL}/menuRoutes/live-root-menu-ids`, {
              params: { root_menu_id: ROOT_MENU_ID },
              headers: authHeader,
            })
          );
        }
        const [childrenRes, pagesRes, liveIdsRes] = await Promise.all(requests);
        setRootMenus(childrenRes.data);
        setRootBannerPages(
          pagesRes.data.filter((p) => p.page_type === 'Header' && p.display_text)
        );
        if (liveMode && liveIdsRes) {
          setLiveRootIds(new Set(liveIdsRes.data));
        }
      } catch {
        setRootMenus([]);
      } finally {
        setRootLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When a Level-1 tile is clicked: enter tab/drill-down view
  const handleRootSelect = useCallback(async (menu) => {
    if (menu.page_link) {
      navigate(menu.page_link);
      return;
    }

    setSelectedRoot(menu);
    setProducts([]);

    const children = await fetchChildren(menu.id);
    const initialPath = [{ parentId: menu.id, selectedId: 'all' }];
    setPath(initialPath);

    // children.length > 0 means there are sub-tabs; show products for the whole subtree
    // children.length === 0 means it's already a leaf
    fetchProducts(menu.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchChildren, fetchProducts, navigate]);

  // Fetch products whenever path changes (tab selection changes)
  useEffect(() => {
    if (!selectedRoot || path.length === 0) return;
    const last = path[path.length - 1];
    const scopeId = last.selectedId === 'all' ? last.parentId : last.selectedId;
    fetchProducts(scopeId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  const handleTabSelect = useCallback(async (levelIndex, menuId) => {
    if (menuId === 'all') {
      setPath((prev) => [
        ...prev.slice(0, levelIndex),
        { ...prev[levelIndex], selectedId: 'all' },
      ]);
      return;
    }

    const children = await fetchChildren(menuId);

    setPath((prev) => {
      const updated = [
        ...prev.slice(0, levelIndex),
        { ...prev[levelIndex], selectedId: menuId },
      ];
      if (children.length > 0) {
        updated.push({ parentId: menuId, selectedId: 'all' });
      }
      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchChildren]);

  const handleBack = () => {
    setSelectedRoot(null);
    setPath([]);
    setProducts([]);
    setMenuPages([]);
    setPacks([]);
    setProductsError(null);
  };

  // â”€â”€ Render â”€â”€
  const visibleMenus = liveRootIds
    ? rootMenus.filter((m) => liveRootIds.has(m.id))
    : rootMenus;

  const searchBar = (
    <div className={`search-wrapper${scrolled ? ' search-wrapper--shrunk' : ''}`}>
      <div className="search-bar">
        <span className="search-icon">&#128269;</span>
        <input
          ref={searchInputRef}
          type="text"
          className="search-input"
          placeholder="Search products..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        {searchText && (
          <button className="search-clear" onClick={handleSearchClear} aria-label="Clear search">
            &#10005;
          </button>
        )}
        {searchText && (
          <button className="search-reset" onClick={handleSearchClear} aria-label="Reset search">
            Reset
          </button>
        )}
      </div>
    </div>
  );

  // While search is active, overlay results regardless of drill-down state
  if (searchText) {
    return (
      <div className="categories-container">
        {searchBar}
        {searchLoading && (
          <div className="cat-state">
            <div className="cat-spinner" />
            <p>Loading products&hellip;</p>
          </div>
        )}
        {!searchLoading && searchError && (
          <div className="cat-state cat-state--error"><p>{searchError}</p></div>
        )}
        {!searchLoading && !searchError && searchProducts.length === 0 && (
          <div className="cat-state">
            <p>No products found for &ldquo;{searchText}&rdquo;</p>
          </div>
        )}
        {!searchLoading && !searchError && searchProducts.length > 0 && (
          <ProductGrid
            products={searchProducts}
            loading={false}
            error={null}
            user={user}
            isLive={isLive}
          />
        )}
      </div>
    );
  }

  if (!selectedRoot) {
    return (
      <div className="categories-container">
        {searchBar}
        <PageList pages={rootBannerPages} />
        <RootTiles
          menus={visibleMenus}
          loading={rootLoading}
          onSelect={handleRootSelect}
        />
      </div>
    );
  }

  return (
    <div className="categories-container">
      {searchBar}

      {/* Header bar with back button + selected category name */}
      <div className="cat-drill-header">
        <button className="cat-back-btn" onClick={handleBack}>
          &#8592;
        </button>
        <span className="cat-drill-title">{selectedRoot.menu_name}</span>
      </div>

      {/* Stacked tab rows â€” hidden for leaf nodes (no children) */}
      {path.map((level, i) => {
        const children = menuMap[level.parentId] || [];
        const isLoading = !!menuLoading[level.parentId];
        if (!isLoading && children.length === 0) return null;
        return (
          <TabRow
            key={level.parentId}
            menus={children}
            selectedId={level.selectedId}
            onSelect={(menuId) => handleTabSelect(i, menuId)}
            loading={isLoading}
          />
        );
      })}

      <PackGrid packs={packs} user={user} />
      <ProductGrid
        products={products}
        loading={productsLoading}
        error={productsError}
        menuPages={menuPages}
        user={user}
        isLive={isLive}
      />
    </div>
  );
};

export default Categories;
